import { filter, get, map } from 'lodash';
import moment from 'moment';

import { PGInvitationStatus } from '@appTypes/enums';
import type {
  GetParentGuardianInvitationDetailsResponse,
  GetParentGuardianInvitationResponse,
  GetRelationshipsResponse,
  GetYouthAdventuresRequest,
  GetYouthAdventuresResponse,
  GetYouthAwardsRequest,
  GetYouthAwardsResponse,
  GetYouthMeritBadgesRequest,
  GetYouthMeritBadgesResponse,
  GetYouthRanksAdvancementRequest,
  GetYouthRanksAdvancementResponse,
  GetYouthSSElectivesResponse,
  PostParentGuardianInvitationAcceptRequest,
  PostParentGuardianInvitationRequest,
  PostPersonSearchByMemberIdRequest,
  PostPersonSearchByMemberIdResponse,
  PostUserMeritBadgeBlueCardsReq,
  PostUserMeritBadgeBlueCardsRes,
} from '@appTypes/esb';
import type {
  NormalizedPostTransformedPersonSearchResponse,
  PostPersonSearchRequest,
  PostPersonSearchResponse,
} from '@appTypes/webscript';
import { tagTypes } from '@modules/rtk/constants';
import { esbApi } from '@modules/rtk/esb-api';
import tagFactory from '@modules/rtk/utils/tagFactory';
import { webscriptApi } from '@modules/rtk/webscript-api';
import { filterRelationships } from '@modules/shared/utils';

export const youthProfileWebScriptApi = webscriptApi.injectEndpoints({
  endpoints: builder => ({
    /**
     * @webscriptEndpoint POST /vim2/WebScript/Output/proxy.aspx?Method=ExecuteScriptSet&ScriptSetCode=MYSTAccount&ScriptCode=PersonSearch
     */
    postPersonSearch: builder.mutation<
      NormalizedPostTransformedPersonSearchResponse,
      {
        payload: PostPersonSearchRequest;
      }
    >({
      query: arg => ({
        url: '',
        method: 'POST',
        body: {
          FirstName: arg.payload.firstName,
          LastName: arg.payload.lastName,
          DateOfBirth: moment(arg.payload.dateOfBirth).format('YYYYMMDD000000'),
          ZipCode: arg.payload.zipCode,
        },
        params: {
          Method: 'ExecuteScriptSet',
          ScriptSetCode: 'MYSTAccount',
          ScriptCode: 'PersonSearch',
        },
      }),
      transformResponse: (result: PostPersonSearchResponse) =>
        map(get(result, 'Users', []), user => ({
          councilName: get(user, 'CouncilName'),
          dateOfBirth: get(user, 'DateOfBirth'),
          emailAddress: get(user, 'EmailAddress'),
          firstName: get(user, 'FirstName'),
          lastName: get(user, 'LastName'),
          memberId: get(user, 'MemberID'),
          hasUsername: get(user, 'HasUsername') === 'true',
          referenceUserGUID: get(user, 'ReferenceUserGUID'),
          zipCode: get(user, 'ZipCode'),
        })),
    }),
  }),
});

export const youthProfileApi = esbApi.injectEndpoints({
  endpoints: builder => ({
    /**
     * @esbEndpoint GET /advancements/v2/youth/:userId/adventures
     */
    getYouthAdventures: builder.query<
      GetYouthAdventuresResponse,
      {
        userId: string;
        params: GetYouthAdventuresRequest;
      }
    >({
      query: arg => ({
        url: `/advancements/v2/youth/${arg.userId}/adventures`,
        method: 'GET',
        params: arg.params,
      }),
      providesTags: (_result, error, arg) =>
        error
          ? []
          : [
              {
                type: tagTypes.YouthAdventure,
                id: arg.userId,
              },
            ],
    }),
    /**
     * @esbEndpoint GET /advancements/v2/youth/:userId/awards
     */
    getYouthAwards: builder.query<
      GetYouthAwardsResponse,
      {
        userId: string;
        params: GetYouthAwardsRequest;
      }
    >({
      query: arg => ({
        url: `/advancements/v2/youth/${arg.userId}/awards`,
        method: 'GET',
      }),
      providesTags: (result, error, arg) =>
        result && !error
          ? [tagFactory.YouthAward.youthAwardsList(arg.userId)]
          : [],
    }),
    /**
     * @esbEndpoint GET /advancements/v2/youth/:userId/meritBadges
     */
    getYouthMeritBadges: builder.query<
      GetYouthMeritBadgesResponse,
      {
        userId: string | number;
        params: GetYouthMeritBadgesRequest;
      }
    >({
      query: arg => ({
        url: `/advancements/v2/youth/${arg.userId}/meritBadges`,
        method: 'GET',
      }),
      providesTags: (result, error, arg) =>
        result && !error
          ? [tagFactory.YouthMeritBadge.youthMBList(arg.userId)]
          : [],
    }),
    /**
     * @esbEndpoint GET /advancements/v2/youth/:userId/ranks
     */
    getYouthRanks: builder.query<
      GetYouthRanksAdvancementResponse,
      {
        userId: string;
        params: GetYouthRanksAdvancementRequest;
      }
    >({
      query: arg => ({
        url: `/advancements/v2/youth/${arg.userId}/ranks`,
        method: 'GET',
      }),
      providesTags: (result, error, arg) =>
        result && !error
          ? [
              {
                type: tagTypes.YouthRanks,
                id: arg.userId,
              },
            ]
          : [],
    }),
    /**
     * @esbEndpoint GET /advancements/v2/youth/:userId/ssElectives
     */
    getYouthSSElectives: builder.query<
      GetYouthSSElectivesResponse,
      {
        userId: string;
      }
    >({
      query: arg => ({
        url: `/advancements/v2/youth/${arg.userId}/ssElectives`,
        method: 'GET',
      }),
      providesTags: (result, error, arg) =>
        result && !error
          ? [
              {
                type: tagTypes.YouthSSElectives,
                id: arg.userId,
              },
            ]
          : [],
    }),
    /**
     * @esbEndpoint POST /advancements/v2/organization/:organizationGuid/meritBadges/printBlueCard
     */
    postMeritBadgeBlueCards: builder.query<
      PostUserMeritBadgeBlueCardsRes,
      {
        organizationGuid: string;
        payload: PostUserMeritBadgeBlueCardsReq;
      }
    >({
      query: arg => ({
        url: `/advancements/v2/organization/${arg.organizationGuid}/meritBadges/printBlueCard`,
        method: 'POST',
        body: arg.payload,
        responseHandler: response => response.blob(),
      }),
    }),

    /**
     * @esbEndpoint POST /persons/v2/relationshipSearch
     */
    postPersonSearchByMemberId: builder.mutation<
      PostPersonSearchByMemberIdResponse,
      {
        payload: PostPersonSearchByMemberIdRequest;
      }
    >({
      query: arg => ({
        url: '/persons/v2/relationshipSearch',
        method: 'POST',
        body: arg.payload,
      }),
      transformResponse: (result: PostPersonSearchByMemberIdResponse) => ({
        ...result,
        gender: get(result, 'gender') === 'male' ? 'M' : 'F',
      }),
    }),

    /**
     * @esbEndpoint GET /persons/v2/:personGuid/relationships?includeIneligibles=false
     */
    getRelationships: builder.query<
      GetRelationshipsResponse,
      {
        personGuid: string;
      }
    >({
      query: arg => ({
        url: `/persons/v2/${arg.personGuid}/relationships?includeIneligibles=false`,
        method: 'GET',
      }),
      transformResponse: (result: GetRelationshipsResponse) =>
        filterRelationships(result),
      providesTags: (result, error, arg) =>
        result && !error
          ? [
              {
                type: tagTypes.Relationships,
                id: arg.personGuid,
              },
            ]
          : [],
    }),

    /**
     * @esbEndpoint DELETE /persons/v2/:personGuid/relationships/:relationshipId
     */
    deleteRelationship: builder.mutation<
      void,
      {
        relationshipId: string;
        personGuid: string;
      }
    >({
      query: arg => ({
        url: `/persons/v2/${arg.personGuid}/relationships/${arg.relationshipId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, arg) =>
        result && !error
          ? [
              {
                type: tagTypes.Relationships,
                id: arg.personGuid,
              },
            ]
          : [],
    }),

    /**
     * @esbEndpoint GET /persons/v2/:personGuid/parentGuardianInvitation
     */
    getParentGuardianInvitation: builder.query<
      GetParentGuardianInvitationResponse,
      {
        personGuid: string;
      }
    >({
      query: arg => ({
        url: `/persons/v2/${arg.personGuid}/parentGuardianInvitation`,
        method: 'GET',
      }),
      providesTags: (result, error, arg) =>
        result && !error
          ? [
              {
                type: tagTypes.ParentGuardianInvitation,
                id: arg.personGuid,
              },
            ]
          : [],
      transformResponse: (result: GetParentGuardianInvitationResponse) =>
        filter(
          result,
          invitation =>
            invitation.invitationStatusLookupLong ===
              PGInvitationStatus.Initiated ||
            invitation.invitationStatusLookupLong ===
              PGInvitationStatus.Submitted,
        ),
    }),

    /**
     * @esbEndpoint GET /persons/v2/:personGuid/parentGuardianInvitation/:invitationId
     */
    getParentGuardianInvitationDetails: builder.query<
      GetParentGuardianInvitationDetailsResponse,
      {
        personGuid: string;
        invitationId: number;
      }
    >({
      query: arg => ({
        url: `/persons/v2/${arg.personGuid}/parentGuardianInvitation/${arg.invitationId}`,
        method: 'GET',
      }),
    }),

    /**
     * @esbEndpoint POST /persons/v2/:personGuid/parentGuardianInvitation/create
     */
    postParentGuardianInvitation: builder.mutation<
      void,
      {
        payload: PostParentGuardianInvitationRequest;
        personGuid: string;
      }
    >({
      query: arg => ({
        url: `/persons/v2/${arg.personGuid}/parentGuardianInvitation/create`,
        method: 'POST',
        body: arg.payload,
      }),
      invalidatesTags: (result, error, arg) =>
        result && !error
          ? [
              {
                type: tagTypes.ParentGuardianInvitation,
                id: arg.personGuid,
              },
            ]
          : [],
    }),

    /**
     * @esbEndpoint DELETE /persons/v2/:personGuid/parentGuardianInvitation/:parentRequestId
     */
    deleteParentGuardianInvitation: builder.mutation<
      void,
      {
        personGuid: string;
        parentRequestId: number;
      }
    >({
      query: arg => ({
        url: `/persons/v2/${arg.personGuid}/parentGuardianInvitation/${arg.parentRequestId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, arg) =>
        result && !error
          ? [
              {
                type: tagTypes.ParentGuardianInvitation,
                id: arg.personGuid,
              },
            ]
          : [],
    }),

    /**
     * @esbEndpoint POST /persons/v2/:personGuid/parentGuardianInvitation/:parentRequestId/accept
     */
    postParentGuardianInvitationAccept: builder.mutation<
      void,
      {
        payload: PostParentGuardianInvitationAcceptRequest;
        personGuid: string;
        parentRequestId: number;
      }
    >({
      query: arg => ({
        url: `/persons/v2/${arg.personGuid}/parentGuardianInvitation/${arg.parentRequestId}/accept`,
        method: 'POST',
        body: arg.payload,
      }),
    }),
  }),
});

export const {
  useLazyPostMeritBadgeBlueCardsQuery,
  usePostPersonSearchByMemberIdMutation,
  useGetRelationshipsQuery,
  useDeleteRelationshipMutation,
  useGetParentGuardianInvitationQuery,
  useGetParentGuardianInvitationDetailsQuery,
  usePostParentGuardianInvitationMutation,
  useDeleteParentGuardianInvitationMutation,
  usePostParentGuardianInvitationAcceptMutation,
  useGetYouthRanksQuery,
  useGetYouthMeritBadgesQuery,
  useLazyGetYouthMeritBadgesQuery,
  useGetYouthAwardsQuery,
  useLazyGetYouthAwardsQuery,
  useGetYouthAdventuresQuery,
} = youthProfileApi;

export const { usePostPersonSearchMutation } = youthProfileWebScriptApi;
